import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Input } from "@/pages/components/input";
import { Label } from "@/components/ui/label";

type PasswordInputProps = {
  id: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  "data-testid"?: string;
  autoComplete?: string;
};

const PasswordInput = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  "data-testid": dataTestId,
  autoComplete = "current-password",
}: PasswordInputProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const Icon = isVisible ? Eye : EyeOff;
  const errorId = `${id}-error`;
  const hasError = Boolean(error);

  return (
    <div className="grid w-full items-center gap-3">
      {/*
        3.3.2 Labels or Instructions — A. The `label` prop was accepted by this component
        and then never rendered, so a password field here had no label at all — only a
        placeholder, which disappears as soon as the member types.
      */}
      {label && <Label htmlFor={id}>{label}</Label>}

      <div className="relative flex flex-row items-center space-x-2">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          aria-invalid={hasError ? true : undefined}
          aria-describedby={hasError ? errorId : undefined}
          icon={
            /*
              2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A. The show/hide toggle was a
              bare Lucide <svg> with an onClick: not focusable, no role, no name — so a
              keyboard-only member could never reveal what they had typed. aria-pressed
              exposes the toggle state.
            */
            <button
              type="button"
              onClick={() => setIsVisible(!isVisible)}
              aria-pressed={isVisible}
              aria-label={isVisible ? "Hide password" : "Show password"}
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <Icon aria-hidden="true" className="size-4 text-black" />
            </button>
          }
          type={isVisible ? "text" : "password"}
          autoComplete={autoComplete}
          iconPosition="right"
          placeholder={placeholder}
          className="border-0 bg-gray-100 focus-visible:ring-gray-200"
          data-testid={dataTestId}
          maxLength={64}
        />
      </div>

      {/*
        3.3.1 Error Identification — A. Two defects here: the message was only reachable by
        hovering a non-focusable svg, and it was gated on `!label` — so passing a label
        silently suppressed the error entirely. It is now always rendered as text, tied to
        the input via aria-describedby, and announced via role="alert".
      */}
      {hasError && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}

      {/*
        4.1.3 Status Messages — AA. aria-pressed/aria-label on the toggle button change
        its name, but that's only reliably re-announced if focus is still on the button
        when it happens — some screen reader/browser pairings don't re-announce a toggle
        button's own name change consistently. This live region is mounted once, before
        any toggle, so only its text changes — an explicit, redundant confirmation that
        doesn't depend on that.
      */}
      <div role="status" aria-live="polite" className="sr-only">
        {isVisible ? "Password is visible" : "Password is hidden"}
      </div>
    </div>
  );
};

export default PasswordInput;
